import { AfterViewInit, Component, OnInit, inject } from '@angular/core';
import { ArabicHandleService, AuthenticationService, IdleTimeoutService } from '@doc/api/server-bo';
import { EnvConfig, LanguageService } from '@doc/core';
import { NavigationEnd, Router } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { AuthenticationResult, PublicClientApplication } from '@azure/msal-browser';
import { Popover } from 'bootstrap';

@Component({
  selector: 'doc-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  standalone: false,
})
export class AppComponent implements OnInit, AfterViewInit {
  title = 'das';
  showAuthenticatedWidgets = false;
  private envConfig = inject(EnvConfig);

  constructor(
    public language: LanguageService,
    private arabicHandleService: ArabicHandleService,
    private msalService: MsalService,
    private authService: AuthenticationService,
    private router: Router,
    private idleService: IdleTimeoutService
  ) {
    Array.from(document.querySelectorAll('[data-bs-toggle="popover"]'))
      .forEach(popoverNode => new Popover(popoverNode));
      this.updateShellState(this.router.url);
      router.events.subscribe(event => {
    if (event instanceof NavigationEnd) {
      this.updateShellState(event.urlAfterRedirects);
      if (this.showAuthenticatedWidgets) {
        this.idleService.startWatching();
      } else {
        this.idleService.stopWatching();
      }
    }
  });
  }

  async ngOnInit(): Promise<void> {
    const language = localStorage.getItem('lang');
    const lang = language && language !== 'undefined' ? language : 'en';
    this.arabicHandleService.arabicManagement(lang);

    // Handle Azure AD redirect at app root so we can trigger SSO invitation flow
    const msalInstance = this.msalService.instance as PublicClientApplication;
    await msalInstance.initialize();

    const redirectResponse = await msalInstance.handleRedirectPromise();
    if (redirectResponse) {
      msalInstance.setActiveAccount(redirectResponse.account);

      const code = sessionStorage.getItem('ssoInvitationCode');
      const email = sessionStorage.getItem('ssoInvitationEmail');
      const returnUrl = sessionStorage.getItem('ssoReturnUrl') || '/main';

      if (code || email) {
        // Handle invitation SSO flow in app component
        await this.handleInvitationSso(code, email, returnUrl);
      } else {
        // For regular SSO login, redirect to SSO login component to process the redirect
        // Store the redirect response info so SSO component can use it
        const currentUrl = this.router.url;
        if (!currentUrl.startsWith('/auth/sso-login')) {
          this.router.navigate(['/auth/sso-login'], {
            queryParams: { returnUrl: returnUrl },
            replaceUrl: true
          });
        }
      }
    }
  }

  ngAfterViewInit() {
    const observer = new MutationObserver(() => {
      const backdrop = document.querySelector('.modal-backdrop');
      if (backdrop && backdrop.parentElement !== document.body) {
        document.body.appendChild(backdrop);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  private async handleInvitationSso(
    code: string | null,
    email: string | null,
    returnUrl: string
  ): Promise<void> {
    const token = await this.getAccessToken();
    if (!token) {
      console.error('Failed to acquire access token for invitation SSO');
      return;
    }

    const payload: any = { AzureToken: token };
    if (code) payload.InvitationCode = code;
    if (email) payload.Email = email;

    this.authService.loginsso(payload).subscribe({
      next: (response: any) => {
        if (response.status) {
          const targetUrl = returnUrl || '/main';
          // Navigate with error handling for ChunkLoadError
          this.router.navigate([targetUrl]).catch((error: any) => {
            // Handle ChunkLoadError - usually caused by stale cache after deployment
            if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk') || error?.message?.includes('ChunkLoadError')) {
              console.warn('ChunkLoadError detected, reloading page to clear cache:', error);
              // Reload the page to clear cache and retry navigation
              window.location.href = targetUrl;
            } else {
              console.error('Navigation error:', error);
              // Retry navigation after a short delay
              setTimeout(() => {
                this.router.navigate([targetUrl]).catch((retryError: any) => {
                  console.error('Navigation retry failed:', retryError);
                  // If retry also fails, reload the page as last resort
                  window.location.href = targetUrl;
                });
              }, 1000);
            }
          });
        } else {
          this.router.navigate(['/auth/login']);
        }
      },
      error: () => {
        this.router.navigate(['/auth/login']);
      }
    });
  }

  private async getAccessToken(): Promise<string | null> {
    try {
      const response = await this.msalService
        .acquireTokenSilent({
          scopes: this.envConfig.azureAd?.apiScopes || []
        })
        .toPromise();
      return (response as AuthenticationResult | null)?.accessToken || null;
    } catch (error) {
      console.error('Error acquiring token silently at root:', error);
      return null;
    }
  }

  private updateShellState(url: string): void {
    const normalizedUrl = url || '';
    const isAuthRoute = normalizedUrl.startsWith('/auth');
    const hasAccessToken = !!localStorage.getItem('accessToken');
    this.showAuthenticatedWidgets = !isAuthRoute && hasAccessToken;
  }

}
